import { useCallback, useEffect, useRef, useState } from 'react'

/**
 * Hook to manage a controlled input and a debounced value separately.
 * - controlledValue: the current input value for immediate UI update
 * - debouncedValue: the value updated only after the debounce delay
 */
export const useDebouncedSearchFilter = (
  initialValue: string,
  delay = 1000,
): {
  controlledValue: string
  setControlledValue: (val: string) => void
  debouncedValue: string
} => {
  const [controlledValue, setControlledValue] = useState(initialValue)
  const [debouncedValue, setDebouncedValue] = useState(initialValue)

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(controlledValue)
    }, delay)

    return () => {
      clearTimeout(handler)
    }
  }, [controlledValue, delay])

  return { controlledValue, setControlledValue, debouncedValue }
}

/**
 * Creates a debounce function, can be used only for single callback, if we have 2 different callbacks we need to call this hook
 * 2 times.
 * @returns the function for debouncing
 */
export const useDebounce = () => {
  const timeoutRef = useRef<NodeJS.Timeout | null>(null)

  const debouncedCallback = useCallback((callback: (...args: any) => any, delay: number) => {
    // Clear previous timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current)
    }

    // Set a new timeout
    timeoutRef.current = setTimeout(() => {
      callback()
    }, delay)
  }, [])

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
      }
    }
  }, [])

  return debouncedCallback
}
